// src/pages/DashboardPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  startBot,
  stopBot,
  submitRequestToken,
  getDashboardData,
  triggerInstrumentRefresh,
  panicSquareOffAll,
  getZerodhaLoginUrl,
} from "../services/api";

function DashboardPage() {
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshingInstruments, setIsRefreshingInstruments] = useState(false);
  const [isPanicking, setIsPanicking] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [uiMessage, setUiMessage] = useState("");
  const [requestToken, setRequestToken] = useState("");

  const [searchParams, setSearchParams] = useSearchParams();

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await getDashboardData();
      setDashboardData(response.data);
      setErrorMessage("");
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setErrorMessage(
        error.response?.data?.error ||
          "Could not fetch dashboard data. Is the backend running?"
      );
    }
  }, []);

  useEffect(() => {
    const authStatus = searchParams.get("auth_status");
    const errorMsgParam = searchParams.get("error_message");

    if (authStatus === "success") {
      setUiMessage(
        "Zerodha authentication successful! Fetching latest data..."
      );
      setSearchParams({}, { replace: true });
    } else if (authStatus === "failure") {
      setErrorMessage(
        `Zerodha authentication failed: ${decodeURIComponent(
          errorMsgParam || "Unknown error"
        )}`
      );
      setSearchParams({}, { replace: true });
    }

    setIsLoading(true);
    fetchDashboardData().finally(() => setIsLoading(false));

    const intervalId = setInterval(fetchDashboardData, 15000);
    return () => clearInterval(intervalId);
  }, [fetchDashboardData, searchParams, setSearchParams]);

  const handleLoginWithZerodha = async () => {
    setUiMessage("Getting Zerodha login URL...");
    setErrorMessage("");
    try {
      const response = await getZerodhaLoginUrl();
      if (response.data && response.data.login_url) {
        window.location.href = response.data.login_url;
      } else {
        throw new Error("Login URL not received from backend.");
      }
    } catch (error) {
      console.error("Error fetching Zerodha login URL:", error);
      setUiMessage("");
      setErrorMessage(
        error.response?.data?.error ||
          "Could not get Zerodha login URL from server."
      );
    }
  };

  const handleStartBot = async () => {
    setUiMessage("Starting bot...");
    setErrorMessage("");
    try {
      const response = await startBot();
      setUiMessage(response.data.message || "Bot start command sent.");
      fetchDashboardData();
    } catch (error) {
      console.error("Error starting bot:", error);
      setUiMessage("");
      setErrorMessage(error.response?.data?.error || "Could not start bot.");
    }
  };

  const handleStopBot = async () => {
    setUiMessage("Stopping bot...");
    setErrorMessage("");
    try {
      const response = await stopBot();
      setUiMessage(response.data.message || "Bot stop command sent.");
      fetchDashboardData();
    } catch (error) {
      console.error("Error stopping bot:", error);
      setUiMessage("");
      setErrorMessage(error.response?.data?.error || "Could not stop bot.");
    }
  };

  const handleSubmitToken = async (e) => {
    e.preventDefault();
    if (!requestToken) {
      setUiMessage("Please enter the request token.");
      return;
    }
    setUiMessage("Submitting request token...");
    setErrorMessage("");
    try {
      const response = await submitRequestToken(requestToken);
      setUiMessage(response.data.message || "Request token submitted.");
      setRequestToken("");
      setTimeout(fetchDashboardData, 2000);
    } catch (error) {
      console.error("Error submitting request token:", error);
      setUiMessage("");
      setErrorMessage(error.response?.data?.error || "Could not submit token.");
    }
  };

  const handleInstrumentRefresh = async () => {
    setIsRefreshingInstruments(true);
    setUiMessage("Requesting instrument refresh...");
    setErrorMessage("");
    try {
      const response = await triggerInstrumentRefresh();
      setUiMessage(
        response.data.message ||
          "Instrument refresh process completed/triggered."
      );
    } catch (error) {
      console.error("Error triggering instrument refresh:", error);
      setUiMessage("");
      setErrorMessage(
        error.response?.data?.error || "Could not trigger instrument refresh."
      );
    } finally {
      setIsRefreshingInstruments(false);
    }
  };

  const handlePanicSquareOff = async () => {
    const currentModeDisplay =
      dashboardData?.tradingMode === "PAPER" ? "PAPER" : "LIVE";
    const confirmPanic = window.confirm(
      `ARE YOU SURE you want to attempt to square off ALL open ${currentModeDisplay} bot positions immediately? This action cannot be undone.`
    );

    if (confirmPanic) {
      setIsPanicking(true);
      setUiMessage(
        `Attempting Panic Square Off for ${currentModeDisplay} positions...`
      );
      setErrorMessage("");
      try {
        const response = await panicSquareOffAll();
        setUiMessage(
          response.data.message ||
            "Panic square off command sent. Check positions and logs."
        );
        fetchDashboardData();
      } catch (error) {
        console.error("Error during panic square off:", error);
        setUiMessage("");
        setErrorMessage(
          error.response?.data?.error || "Could not execute panic square off."
        );
      } finally {
        setIsPanicking(false);
      }
    } else {
      setUiMessage("Panic square off cancelled by user.");
    }
  };

  const renderAccountFunds = () => {
    if (!dashboardData) return <p>Loading funds info...</p>;

    const modePrefix = dashboardData.tradingMode === "PAPER" ? "Paper " : "";
    if (
      dashboardData.tradingMode === "LIVE" &&
      !dashboardData.zerodhaAuthenticated
    ) {
      return <p>Live Zerodha session not authenticated.</p>;
    }

    // --- THIS IS THE CORRECTED LINE ---
    const availableCash = dashboardData.margins?.available?.cash ?? "N/A";

    return (
      <div>
        <h4>{modePrefix}Account Funds</h4>
        <p>
          Available Cash (Equity):{" "}
          {typeof availableCash === "number"
            ? availableCash.toFixed(2)
            : availableCash}
        </p>
      </div>
    );
  };

  if (isLoading && !dashboardData) {
    return <p>Loading Dashboard...</p>;
  }

  const currentMode = dashboardData?.tradingMode || "N/A";
  const modePrefixDisplay =
    currentMode === "PAPER" ? "Paper " : currentMode === "LIVE" ? "Live " : "";

  return (
    <div style={{ fontFamily: "Arial, sans-serif", padding: "20px" }}>
      <h1>Nifty Scalper Bot Dashboard</h1>

      <p
        style={{
          textAlign: "center",
          fontSize: "1.2em",
          fontWeight: "bold",
          color: currentMode === "PAPER" ? "orange" : "green",
          border: "1px solid",
          padding: "5px",
          borderRadius: "5px",
          margin: "10px 0 20px 0",
        }}
      >
        Current Trading Mode: {currentMode.toUpperCase()}
      </p>

      {uiMessage && (
        <p
          style={{
            color: "blue",
            backgroundColor: "#e6f7ff",
            padding: "10px",
            borderRadius: "5px",
            border: "1px solid #b3d7ff",
          }}
        >
          <em>{uiMessage}</em>
        </p>
      )}
      {errorMessage && (
        <p
          style={{
            color: "red",
            backgroundColor: "#ffe6e6",
            padding: "10px",
            borderRadius: "5px",
            border: "1px solid #ffb3b3",
          }}
        >
          <strong>Error: {errorMessage}</strong>
        </p>
      )}

      <button
        onClick={() => {
          setIsLoading(true);
          fetchDashboardData().finally(() => setIsLoading(false));
        }}
        disabled={isLoading}
      >
        {isLoading && !isRefreshingInstruments && !isPanicking
          ? "Refreshing Dashboard..."
          : "Refresh Dashboard Data"}
      </button>

      <hr style={{ margin: "20px 0" }} />

      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <div
          style={{
            minWidth: "300px",
            padding: "10px",
            border: "1px solid #ddd",
            borderRadius: "5px",
          }}
        >
          <h2>Bot Control</h2>
          <p>
            Bot App Status: <strong>{dashboardData?.botStatus || "N/A"}</strong>{" "}
            (
            {dashboardData?.isMonitoringServicesRunning
              ? "Monitoring Active"
              : "Monitoring Idle"}
            )
          </p>
          <p>
            Live Zerodha Session:{" "}
            {dashboardData?.zerodhaAuthenticated ? (
              <span style={{ color: "green" }}>Authenticated</span>
            ) : (
              <span style={{ color: "red" }}>Not Authenticated</span>
            )}
          </p>
          <button
            onClick={handleStartBot}
            disabled={
              dashboardData?.botStatus === "ACTIVE" ||
              !dashboardData?.zerodhaAuthenticated ||
              isLoading ||
              isPanicking
            }
            style={{ marginRight: "10px", padding: "8px 12px" }}
          >
            Start Bot
          </button>
          <button
            onClick={handleStopBot}
            disabled={
              dashboardData?.botStatus !== "ACTIVE" || isLoading || isPanicking
            }
            style={{ padding: "8px 12px" }}
          >
            Stop Bot
          </button>
        </div>

        <div
          style={{
            minWidth: "300px",
            padding: "10px",
            border: "1px solid #ddd",
            borderRadius: "5px",
          }}
        >
          <h2>Daily Zerodha Setup</h2>
          {!dashboardData?.zerodhaAuthenticated ? (
            <div>
              {currentMode === "PAPER" ? (
                <p>
                  <strong>Paper Mode:</strong> A daily Zerodha login is still
                  required to get the live market data needed for simulation.
                </p>
              ) : (
                <p>
                  To use LIVE mode, please authenticate your Zerodha session for
                  the day.
                </p>
              )}
              <button
                onClick={handleLoginWithZerodha}
                disabled={isLoading}
                style={{
                  padding: "10px 15px",
                  backgroundColor: "#357ae8",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Login with Zerodha
              </button>
            </div>
          ) : (
            <div>
              <p style={{ color: "green", fontWeight: "bold" }}>
                Zerodha session is authenticated and active for the day.
              </p>
              <button
                onClick={handleLoginWithZerodha}
                disabled={isLoading}
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Re-authenticate
              </button>
            </div>
          )}
          <details style={{ marginTop: "20px", fontSize: "0.9em" }}>
            <summary>Manual Token Submission (Fallback)</summary>
            <form
              onSubmit={handleSubmitToken}
              style={{
                display: "flex",
                alignItems: "center",
                marginTop: "10px",
              }}
            >
              <input
                name="requestTokenInput"
                type="text"
                value={requestToken}
                onChange={(e) => setRequestToken(e.target.value)}
                placeholder="Enter request token manually"
                style={{ padding: "8px", marginRight: "10px", width: "250px" }}
                disabled={isLoading || isPanicking}
              />
              <button
                type="submit"
                style={{ padding: "8px 15px" }}
                disabled={isLoading || isPanicking}
              >
                Submit
              </button>
            </form>
          </details>
        </div>
      </div>

      <hr style={{ margin: "20px 0" }} />

      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          textAlign: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <div
          style={{
            minWidth: "200px",
            padding: "10px",
            border: "1px solid #ddd",
            borderRadius: "5px",
          }}
        >
          <h3>Today's {modePrefixDisplay}P&L</h3>
          <p
            style={{
              fontSize: "1.5em",
              color:
                parseFloat(dashboardData?.todaysPnl) >= 0 ? "green" : "red",
            }}
          >
            {dashboardData?.todaysPnl ?? "0.00"}
          </p>
        </div>
        <div
          style={{
            minWidth: "200px",
            padding: "10px",
            border: "1px solid #ddd",
            borderRadius: "5px",
          }}
        >
          {renderAccountFunds()}
        </div>
      </div>

      <hr style={{ margin: "20px 0" }} />

      <div
        style={{
          padding: "10px",
          border: "1px solid #ddd",
          borderRadius: "5px",
        }}
      >
        <h2>Admin Actions</h2>
        <div
          style={{
            marginBottom: "15px",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <button
            onClick={handleInstrumentRefresh}
            disabled={isRefreshingInstruments || isLoading || isPanicking}
            style={{
              padding: "10px 15px",
              backgroundColor: "#ffc107",
              color: "black",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            {isRefreshingInstruments
              ? "Refreshing Instruments..."
              : "Refresh Instruments Manually"}
          </button>
          <p style={{ fontSize: "0.9em", color: "#666", margin: 0 }}>
            Manually triggers the daily instrument data refresh process.
          </p>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <button
            onClick={handlePanicSquareOff}
            disabled={
              isPanicking || isLoading || !dashboardData?.zerodhaAuthenticated
            }
            style={{
              padding: "10px 15px",
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            {isPanicking
              ? "Squaring Off..."
              : `PANIC: Square Off ${modePrefixDisplay}Positions`}
          </button>
          <p style={{ fontSize: "0.9em", color: "#666", margin: 0 }}>
            Attempts to immediately close all open{" "}
            {modePrefixDisplay.toLowerCase()}bot positions. Requires active
            Zerodha session.
          </p>
        </div>
      </div>

      <hr style={{ margin: "20px 0" }} />

      <div>
        <h2>Active {modePrefixDisplay}Positions</h2>
        {isLoading && !dashboardData?.activePositions && (
          <p>Loading active positions...</p>
        )}
        {!isLoading && dashboardData?.activePositions?.length === 0 && (
          <p>No active {modePrefixDisplay.toLowerCase()}bot positions.</p>
        )}
        {dashboardData?.activePositions?.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #ccc", textAlign: "left" }}>
                <th>Symbol</th>
                <th>Qty</th>
                <th>Entry Price</th>
                <th>Opened At</th>
                <th>SL</th>
                <th>TP</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.activePositions.map((pos) => (
                <tr key={pos.id} style={{ borderBottom: "1px solid #eee" }}>
                  <td>{pos.option_symbol}</td>
                  <td>{pos.quantity}</td>
                  {/* --- FIX: Check if the value is a number before calling toFixed --- */}
                  <td>
                    {typeof pos.entry_price_per_option === "number"
                      ? pos.entry_price_per_option.toFixed(2)
                      : "N/A"}
                  </td>
                  <td>{new Date(pos.trade_opened_at).toLocaleString()}</td>
                  <td>
                    {typeof pos.initial_stop_loss_price_per_option === "number"
                      ? pos.initial_stop_loss_price_per_option.toFixed(2)
                      : "N/A"}
                  </td>
                  <td>
                    {typeof pos.target_profit_price_per_option === "number"
                      ? pos.target_profit_price_per_option.toFixed(2)
                      : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default DashboardPage;
